import { useState } from 'react'
import type { AppUser } from '../types'
import { signInWithGoogle, updateCloudProfile } from '../lib/auth'
import { isFirebaseLeagueMode } from '../lib/firebase'

export type UserProfile = AppUser

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  user: UserProfile | null
  onLogin: (user: UserProfile) => void
  onLogout: () => void
}

const AVATAR_OPTIONS = ['🚀', '⚡', '🧠', '🐯', '🐉', '🎯', '👑', '🦉', '🔥', '🛡️']

export function AuthModal({ isOpen, onClose, user, onLogin, onLogout }: AuthModalProps) {
  const [demoName, setDemoName] = useState('')
  const [editing, setEditing] = useState(false)
  const [nickname, setNickname] = useState(user?.nickname || '')
  const [avatar, setAvatar] = useState(user?.avatar || '🧠')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const startDemo = () => {
    const displayName = demoName.trim() || 'Học sinh demo'
    onLogin({
      uid: `demo-${crypto.randomUUID?.() ?? Date.now()}`,
      displayName,
      nickname: displayName,
      avatar: '🧠',
      email: '',
      provider: 'demo',
      role: 'student',
    })
    onClose()
  }

  const startGoogle = async () => {
    setLoading(true)
    setError('')
    try {
      await signInWithGoogle()
      // Supabase redirects to Google. The auth listener in App owns the returned session.
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Không thể bắt đầu đăng nhập Google.')
      setLoading(false)
    }
  }

  const saveProfile = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!user) return
    const next = { ...user, nickname: nickname.trim() || user.nickname, avatar }
    setLoading(true)
    setError('')
    try {
      if (next.provider === 'supabase' || next.provider === 'firebase') await updateCloudProfile(next)
      onLogin(next)
      setEditing(false)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Không thể cập nhật hồ sơ.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
      <section className="card auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <div className="flex justify-between items-center mb-4 pb-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <h2 id="auth-title" style={{ margin: 0, fontSize: '1.25rem' }}>{user ? 'Hồ sơ học tập' : 'Đăng nhập Math ĐGNL'}</h2>
          <button type="button" className="auth-close" onClick={onClose} aria-label="Đóng">×</button>
        </div>

        {user ? (
          editing ? (
            <form onSubmit={saveProfile}>
              <label className="auth-label">Biệt danh hiển thị<input value={nickname} onChange={event => setNickname(event.target.value)} maxLength={40} /></label>
              <span className="auth-label">Chọn avatar</span>
              <div className="avatar-picker">
                {AVATAR_OPTIONS.map(option => <button key={option} type="button" className={avatar === option ? 'selected' : ''} onClick={() => setAvatar(option)} aria-pressed={avatar === option}>{option}</button>)}
              </div>
              {error && <p className="auth-error" role="alert">{error}</p>}
              <div className="flex gap-2 mt-4">
                <button className="btn btn-primary" disabled={loading}>Lưu thay đổi</button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>Hủy</button>
              </div>
            </form>
          ) : (
            <div className="auth-profile">
              <div className="auth-avatar">{user.avatar}</div>
              <h3>{user.nickname || user.displayName}</h3>
              <p className="text-muted">{user.provider === 'demo' ? 'Chế độ demo trên thiết bị này' : user.email}</p>
              <p className="auth-mode-note">{user.provider === 'demo' ? 'Dữ liệu chỉ lưu trong trình duyệt. Đăng nhập Google để tham gia League và đồng bộ thành tích.' : 'Hồ sơ League đang đồng bộ với tài khoản của bạn.'}</p>
              <div className="flex flex-col gap-2">
                <button className="btn btn-secondary" onClick={() => { setNickname(user.nickname); setAvatar(user.avatar); setEditing(true) }}>Sửa biệt danh & avatar</button>
                <button className="btn btn-secondary auth-logout" onClick={() => { void onLogout(); onClose() }}>Đăng xuất</button>
              </div>
            </div>
          )
        ) : (
          <div>
            <p className="text-muted">{isFirebaseLeagueMode ? 'Đăng nhập Google để tạo biệt danh, thách đấu bạn bè, vào nhóm và leo bảng xếp hạng.' : 'Bạn đang ở local-demo. Có thể bắt đầu ngay; dữ liệu chỉ được lưu trên trình duyệt này.'}</p>
            {isFirebaseLeagueMode ? (
              <>
                <button className="btn btn-primary auth-google-button" onClick={startGoogle} disabled={loading}>{loading ? 'Đang chuyển đến Google…' : 'Tiếp tục với Google'}</button>
                <form onSubmit={event => { event.preventDefault(); startDemo() }}>
                  <label className="auth-label">Hoặc dùng thử ngay trên thiết bị này<input value={demoName} onChange={event => setDemoName(event.target.value)} placeholder="Ví dụ: Minh Anh" maxLength={40} /></label>
                  <button className="btn btn-secondary auth-google-button" type="submit" disabled={loading}>Tiếp tục local-demo</button>
                </form>
              </>
            ) : (
              <form onSubmit={event => { event.preventDefault(); startDemo() }}>
                <label className="auth-label">Tên hiển thị (không bắt buộc)<input value={demoName} onChange={event => setDemoName(event.target.value)} placeholder="Ví dụ: Minh Anh" maxLength={40} /></label>
                <button className="btn btn-primary auth-google-button">Bắt đầu local-demo</button>
              </form>
            )}
            {error && <p className="auth-error" role="alert">{error}</p>}
          </div>
        )}
      </section>
    </div>
  )
}
